import dayjs from "dayjs";
import { LloCasesToFormItem } from "../../../../api";
import {
  EMPTY_FILTERS,
  FILTER_FIELD_LABELS,
  type FilterFieldKey,
  type LloCasesToFormFiltersState,
} from "./types";

export function countActiveFilters(filters: LloCasesToFormFiltersState): number {
  let count = 0;
  (Object.keys(filters) as (keyof LloCasesToFormFiltersState)[]).forEach((key) => {
    const value = filters[key];
    if (
      key === "complaintFiledFrom" ||
      key === "complaintFiledTo" ||
      key === "formByFrom" ||
      key === "formByTo"
    ) {
      if (value != null) count += 1;
      return;
    }
    if (typeof value === "string" && value.trim()) count += 1;
  });
  return count;
}

export function hasActiveFilters(filters: LloCasesToFormFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: LloCasesToFormFiltersState,
  key: keyof LloCasesToFormFiltersState
): LloCasesToFormFiltersState {
  if (
    key === "complaintFiledFrom" ||
    key === "complaintFiledTo" ||
    key === "formByFrom" ||
    key === "formByTo"
  ) {
    return { ...filters, [key]: null };
  }
  return { ...filters, [key]: "" };
}

export function resetFilters(): LloCasesToFormFiltersState {
  return { ...EMPTY_FILTERS };
}

export function uniqueStringOptions(
  rows: LloCasesToFormItem[],
  field: "caseName" | "stage" | "raa" | "ap"
): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = (row[field] ?? "").trim();
    if (value && value !== "N/A") values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function applyFilters(
  rows: LloCasesToFormItem[],
  filters: LloCasesToFormFiltersState
): LloCasesToFormItem[] {
  let data = rows;

  if (filters.caseName) {
    data = data.filter(
      (row) =>
        (row.caseName ?? "").trim().toLowerCase() === filters.caseName.trim().toLowerCase()
    );
  }
  if (filters.stage) {
    data = data.filter(
      (row) => (row.stage ?? "").trim().toLowerCase() === filters.stage.trim().toLowerCase()
    );
  }
  if (filters.raa) {
    data = data.filter(
      (row) => (row.raa ?? "").trim().toLowerCase() === filters.raa.trim().toLowerCase()
    );
  }
  if (filters.ap) {
    data = data.filter(
      (row) => (row.ap ?? "").trim().toLowerCase() === filters.ap.trim().toLowerCase()
    );
  }
  if (filters.searchTerm.trim()) {
    const term = filters.searchTerm.trim().toLowerCase();
    data = data.filter((row) =>
      [
        row.caseName,
        row.displayNumber,
        row.stage,
        row.raa,
        row.ap,
        row.lloExpiration,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }
  if (filters.complaintFiledFrom) {
    data = data.filter(
      (row) =>
        !!row.complaintFiledDate &&
        !dayjs(row.complaintFiledDate).isBefore(filters.complaintFiledFrom, "day")
    );
  }
  if (filters.complaintFiledTo) {
    data = data.filter(
      (row) =>
        !!row.complaintFiledDate &&
        !dayjs(row.complaintFiledDate).isAfter(filters.complaintFiledTo, "day")
    );
  }
  if (filters.formByFrom) {
    data = data.filter(
      (row) => !!row.formByDate && !dayjs(row.formByDate).isBefore(filters.formByFrom, "day")
    );
  }
  if (filters.formByTo) {
    data = data.filter(
      (row) => !!row.formByDate && !dayjs(row.formByDate).isAfter(filters.formByTo, "day")
    );
  }

  return data;
}

export function buildActiveChips(filters: LloCasesToFormFiltersState) {
  const chips: { key: keyof LloCasesToFormFiltersState; label: string }[] = [];

  (Object.keys(FILTER_FIELD_LABELS) as FilterFieldKey[]).forEach((key) => {
    const value = filters[key];
    if (typeof value === "string" && value.trim()) {
      chips.push({
        key,
        label: `${FILTER_FIELD_LABELS[key]}: ${value.trim()}`,
      });
    }
  });

  if (filters.complaintFiledFrom) {
    chips.push({
      key: "complaintFiledFrom",
      label: `Filed from: ${filters.complaintFiledFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.complaintFiledTo) {
    chips.push({
      key: "complaintFiledTo",
      label: `Filed to: ${filters.complaintFiledTo.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.formByFrom) {
    chips.push({
      key: "formByFrom",
      label: `Form by from: ${filters.formByFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.formByTo) {
    chips.push({
      key: "formByTo",
      label: `Form by to: ${filters.formByTo.format("MM/DD/YYYY")}`,
    });
  }

  return chips;
}
